import { isCalculatedPricing, projectCalculatedCustomerLine } from "./calculatedPricing";

/**
 * ICH's Invoice and Portfolio (jsreport ich-selections-2 / ich-selections-portfolio-2) print each
 * invoice line's amount from its value (or qty × rate × discount), and the Portfolio prints no
 * amounts at all. A calculated line can therefore print as the customer sees it everywhere else: one
 * unit at its final amount. Every other remote report still refuses calculated lines
 * (calculatedPrintGuard.js).
 */
export const FINAL_AMOUNT_REPORT_IDS = Object.freeze(["ich-selections-2", "ich-selections-portfolio-2"]);

export const isFinalAmountReport = (templateId) => FINAL_AMOUNT_REPORT_IDS.includes(templateId);

const CALCULATION_KEYS = ["calculation", "calculationAudit", "calculationDefinition"];

const lineLabel = (line) => String(line.description || line.desc || line.name || line.label || "a calculated line").trim();

function finalAmountLine(row, requireAmount) {
	if (!isCalculatedPricing(row)) {
		return row;
	}
	// The customer projection: one unit at the final amount, customer fields only. Its calculated
	// marker goes with the others in withoutCalculationDetails.
	const line = projectCalculatedCustomerLine(row);
	if (line.amountRequired && requireAmount) {
		throw new Error(`Enter the final amount for "${lineLabel(line)}" before printing this report.`);
	}
	return line;
}

function withoutCalculationDetails(value, key, depth) {
	if (Array.isArray(value)) {
		const rows = key === "invoiceItems" ? value.map((row) => finalAmountLine(row, depth === 1)) : value;
		return rows.map((row) => withoutCalculationDetails(row, "", depth + 1));
	}
	if (!value || typeof value !== "object" || value instanceof Date) {
		return value;
	}
	return Object.keys(value).reduce((result, field) => {
		if (CALCULATION_KEYS.includes(field)
			|| (field === "pricingBehavior" && value[field] === "calculatedTotal")
			|| (field === "pricingAmountMode" && value[field] === "fixedTotal")) {
			return result;
		}
		result[field] = withoutCalculationDetails(value[field], field, depth + 1);
		return result;
	}, {});
}

/**
 * The data a final-amount report renders: calculated lines in every invoice line list (the job's,
 * saved estimates', agreement documents') become final-amount lines, and calculation details and
 * markers are dropped everywhere else (catalog hits, audit entries), so nothing calculated reaches
 * the remote template. The job's own lines must all have their final amount.
 *
 * @param {Object} activity The prepared job (after normalizePricingMetadataForPrint).
 * @returns {Object} A detached copy for the report.
 */
export function prepareFinalAmountReportData(activity) {
	return withoutCalculationDetails(activity, "", 0);
}
